import { Stack } from '@mui/material';
import { ReactNode, useCallback, useMemo } from 'react';
import { useWatch } from 'react-hook-form';

import { Button } from '@/components/form/baseInputs/Button';
import { EditableHeader } from '@/components/form/EditableHeader/EditableHeader';
import { useFormContext } from '@/components/react-hook-form';
import { useModalState } from '@/hooks/useModalState';
import { stripAngleBrackets } from '@/utils/formatting/strings';

import {
  BORDER_COLOR,
  HEADER_HEIGHT_PX,
} from '../../PresentationTemplateEditor.constants';
import {
  useBundleKindRequiresConfigModal,
  usePresentationTemplateEditorContext,
} from '../../PresentationTemplateEditor.context';
import { useHandleSubmit } from '../../PresentationTemplateEditor.hooks';
import {
  PresentationTemplateEditorBundle,
  PresentationTemplateEditorFormShape,
} from '../../PresentationTemplateEditor.types';
import {
  getBundleTileHasMoreInformationRequired,
  isBundlePageEditable,
} from './bundleTile/PresentationTemplateBundleTile.utils';
import {
  getTrimmedDisplayName,
  PresentationTemplateStageEditorDisplayNamePrompt,
} from './PresentationTemplateStageEditor.displayNamePrompt';

export function PresentationTemplateStageEditorHeader() {
  const { onClose, isDuplicateFlow, isCreating } =
    usePresentationTemplateEditorContext();
  const { onUpdate, onSaveAsNew } = useHandleSubmit();

  const {
    control,
    setValue,
    formState: { isDirty },
  } = useFormContext<PresentationTemplateEditorFormShape>();
  const [rawDisplayName, bundles, isSystemTemplate, isSharedTemplate] =
    useWatch({
      control,
      name: ['displayName', 'bundles', 'isSystemTemplate', 'isSharedTemplate'],
    });

  // System templates are read-only sources, so always force the save-as-new
  // flow for them — even if the editor was opened without the isDuplicateFlow
  // URL flag. This prevents "Done" from issuing an in-place update.
  const forceSaveAsNew =
    isDuplicateFlow || Boolean(isSystemTemplate) || Boolean(isSharedTemplate);

  const bundleKindRequiresConfigModal = useBundleKindRequiresConfigModal();
  const isBundleConfigurable = useCallback(
    (bundle: PresentationTemplateEditorBundle) =>
      isBundlePageEditable(bundle) ||
      bundleKindRequiresConfigModal(bundle.kind),
    [bundleKindRequiresConfigModal]
  );

  const isSaveDisabled = useMemo(
    () =>
      !bundles?.length ||
      bundles.some(
        (bundle) =>
          isBundleConfigurable(bundle) &&
          getBundleTileHasMoreInformationRequired(bundle)
      ),
    [bundles, isBundleConfigurable]
  );
  // Strip angle brackets and their HTML entity equivalents from the
  // server-provided display name to prevent stored XSS when rendered in
  // ContentEditable's html prop (innerHTML).
  const displayName = stripAngleBrackets(rawDisplayName ?? '');

  const sanitizeDisplayName = useCallback(
    (raw: string) =>
      stripAngleBrackets(raw)
        .replace(/&nbsp;/g, ' ')
        .trim(),
    []
  );

  const onChangeTemplateName = useCallback(
    (newDisplayName: string) => {
      setValue('displayName', sanitizeDisplayName(newDisplayName), {
        shouldDirty: true,
      });
    },
    [sanitizeDisplayName, setValue]
  );

  const onInputTemplateName = useCallback(
    (currentValue: string) => {
      setValue('displayName', sanitizeDisplayName(currentValue), {
        shouldDirty: true,
      });
    },
    [sanitizeDisplayName, setValue]
  );

  const onSaveAsNewTemplate = useCallback(
    (newTemplateName: string) => {
      onSaveAsNew(getTrimmedDisplayName(newTemplateName));
    },
    [onSaveAsNew]
  );

  const [{ isModalOpen }, { openModal, closeModal }] = useModalState();

  const onOpenSaveAsNewModal = useCallback(() => {
    openModal();
  }, [openModal]);

  const onDoneCreating = useCallback(() => {
    onSaveAsNew();
  }, [onSaveAsNew]);

  // "Done" should always be clickable. When the user has unsaved changes we
  // save them; when they have not edited anything we just close the editor to
  // avoid a no-op network call and a misleading "saved" toast.
  const onDone = useCallback(() => {
    if (isDirty) {
      onUpdate();
    } else {
      onClose();
    }
  }, [isDirty, onClose, onUpdate]);

  const body = useMemo<ReactNode>(() => {
    if (isCreating) {
      return (
        <>
          <Button variant="secondary" size="sm" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            size="sm"
            onClick={onDoneCreating}
            disabled={isSaveDisabled}
          >
            Done
          </Button>
        </>
      );
    }

    if (forceSaveAsNew) {
      return (
        <>
          <Button variant="secondary" size="sm" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            size="sm"
            onClick={onOpenSaveAsNewModal}
            disabled={isSaveDisabled}
          >
            Save as new template
          </Button>
        </>
      );
    }

    return (
      <>
        <Button
          variant="transparent"
          size="sm"
          onClick={onOpenSaveAsNewModal}
          disabled={isSaveDisabled}
        >
          Save as new template
        </Button>
        <Button variant="secondary" size="sm" onClick={onClose}>
          Cancel
        </Button>
        <Button
          variant="primary"
          size="sm"
          onClick={onDone}
          disabled={isDirty && isSaveDisabled}
        >
          Done
        </Button>
      </>
    );
  }, [
    isSaveDisabled,
    isCreating,
    isDirty,
    forceSaveAsNew,
    onClose,
    onDone,
    onDoneCreating,
    onOpenSaveAsNewModal,
  ]);

  return (
    <Stack
      direction="row"
      width="100%"
      px={1.5}
      py={2}
      justifyContent="space-between"
      alignItems="center"
      borderBottom={`1px solid ${BORDER_COLOR}`}
      flex={`0 0 ${HEADER_HEIGHT_PX}px`}
    >
      <EditableHeader
        value={displayName}
        onChange={onChangeTemplateName}
        onInput={onInputTemplateName}
        testId="PresentationTemplateStageEditorHeader-displayNameInput"
        typographyVariant="title-l"
      />
      <Stack direction="row" spacing={1} alignItems="center">
        {isModalOpen && (
          <PresentationTemplateStageEditorDisplayNamePrompt
            onClose={closeModal}
            onSubmit={onSaveAsNewTemplate}
          />
        )}
        {body}
      </Stack>
    </Stack>
  );
}
